import { useEffect, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { useTriggerLoad } from "./load";

// Some autocomplete APIs are 2-staged, where the first call fetches suggestions and the second call fetches detailed information based on a selected suggestion.
// Others are single-staged, where the first call fetches all necessary information.
// The type K can either be a key to fetch the next set of information or the information itself.
// If K is the information itself, the pickSuggestion function is merely a mapping function.

export const useInputAutocomplete = <T, K>({
  getSuggestions,
  pickSuggestion,
  setAutocompleteFields,
}: {
  getSuggestions: (
    query: string,
    sessionToken?: string,
  ) => Promise<{ keyToFullObject: K; displayText: string }[]>;
  pickSuggestion: (
    keyToFullObject: K,
    sessionToken?: string,
  ) => Promise<T | undefined | null>;
  setAutocompleteFields: (fields: T) => void;
}) => {
  const [query, setQuery] = useState("");
  const [keyToFullObject, setKeyToFullObject] = useState<K | null>(null);

  const debouncedQuery = useDebounce(query, 500);
  const [sessionToken, setSessionToken] = useState<string>(crypto.randomUUID());

  const [suggestionsLoad, doLoadSuggestions] = useTriggerLoad(async () => {
    if (debouncedQuery.length === 0) {
      return [];
    }
    const suggestions = await getSuggestions(debouncedQuery, sessionToken);
    return suggestions;
  });

  const [selectedItemLoad, doLoadSelectedItem] = useTriggerLoad(async () => {
    if (!keyToFullObject) {
      return null;
    }
    const item = await pickSuggestion(keyToFullObject, sessionToken);
    return item;
  });

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      doLoadSuggestions();
    }
  }, [debouncedQuery, doLoadSuggestions]);

  useEffect(() => {
    if (keyToFullObject) {
      doLoadSelectedItem();
      setSessionToken(crypto.randomUUID());
    }
  }, [keyToFullObject, doLoadSelectedItem, setSessionToken]);

  useEffect(() => {
    if (selectedItemLoad.value) {
      setAutocompleteFields({ ...selectedItemLoad.value });
    }
  }, [selectedItemLoad.value, setAutocompleteFields]);

  return { setQuery, suggestionsLoad, setKeyToFullObject };
};
